import React from 'react';

import styled from 'styled-components';

import { usePackageActions } from '@hooks/ModelsTable/usePackageActions';
import { usePackageDrawers } from '@hooks/ModelsTable/usePackageDrawers';

import { BookSelectorDrawer } from '@components/BookSelectorForPackages';
import ModelDetailsHeaderAgencies from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsHeader/ModelDetailsHeaderAgencies';

import ContactAlertPopover from '@components/ContactAlert/ContactAlertPopover';
import { useContactsSelector } from '@components/ContactsSelector/Common/ContactsSelectorContext';
import AddMediaDrawer from '@components/PackageDrawer/PackageModelCard/AddMediaDrawer/AddMediaDrawer';

import { PackageActionsContainer } from './ModelsTableUI';
import PackageActionIcons from './PackageActionIcons';

export const MobileCell = styled.div`
    min-height: 6rem;

    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.15rem;

    p {
        margin-bottom: 0;
    }

    .name {
        display: flex;
        justify-content: flex-start;
        align-items: center;

        h1 {
            font-size: 1.1rem;
            line-height: 1.1em;
            margin: 0;
            color: ${(props) =>
                props.$status
                    ? props.$status === 'busy'
                        ? props.theme.DangerColor
                        : props.theme.SuccessColor
                    : props.$inPackage
                      ? props.theme.PrimaryColor
                      : 'inherit'};
            text-transform: capitalize;
            margin-right: 8px;
        }
    }

    .detail {
        font-size: ${(props) => props.theme.FontSizeSm};
        margin-top: 4px;
        flex: 1;
    }

    .model-icons {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 1.5rem;
        font-size: 1.2rem;
    }
`;

const ModelsTableColumnPackage = ({
    model,
    newlyAddedToPackage,
    onAddingBooksToPackage,
    onRemovingBookFromPackage,
    onOpenDrawer,
    modelsAvailability,
    isMobile,
    isPackageDrawerCompact,
}) => {
    const { context } = useContactsSelector();

    // Use custom hooks for package actions and drawer management
    const packageActions = usePackageActions(
        model,
        context,
        newlyAddedToPackage,
        onAddingBooksToPackage,
        onRemovingBookFromPackage
    );
    const drawerActions = usePackageDrawers();

    const {
        getModelPlaylists,
        handleAddBook,
        handleDeleteBook,
        currentPackage,
        packageGroup,
        isOpenModelBooksDrawer,
        modelId,
        isMediaDrawer,
    } = { ...packageActions, ...drawerActions };

    // Destructure drawer actions for easier access
    const { handleCloseModelBooksDrawer, handleCloseAddMediaDrawer } = drawerActions;

    const isFetched = modelsAvailability[model.key] && modelsAvailability[model.key].isFetched;
    const isBusy = modelsAvailability[model.key] && modelsAvailability[model.key].isBusy;

    return (
        <>
            {isMobile ? (
                <MobileCell
                    $inPackage={model.PL ? true : false}
                    $status={isFetched ? (isBusy ? 'busy' : 'no-busy') : ''}
                >
                    <div className="name">
                        <h1>{model.name.toLowerCase()}</h1>
                        {model.AL && <ContactAlertPopover contact={model} />}
                    </div>
                    <div className="detail">
                        <ModelDetailsHeaderAgencies readOnly model={model} />
                    </div>
                    <div className="model-icons">
                        {model.PL ? (
                            <PackageActionIcons
                                model={model}
                                isMobile
                                packageActions={packageActions}
                                drawerActions={drawerActions}
                                onOpenDrawer={onOpenDrawer}
                                noTooltip
                            />
                        ) : (
                            <p>no books</p>
                        )}
                    </div>
                </MobileCell>
            ) : model.PL ? (
                <PackageActionsContainer>
                    <PackageActionIcons
                        model={model}
                        packageActions={packageActions}
                        drawerActions={drawerActions}
                        compact={isPackageDrawerCompact}
                    />
                </PackageActionsContainer>
            ) : (
                <p>no books</p>
            )}
            {isOpenModelBooksDrawer && (
                <BookSelectorDrawer
                    contactID={modelId}
                    addedPlaylists={getModelPlaylists(modelId)}
                    packageGroup={packageGroup}
                    packageName={currentPackage.Name}
                    onAddBook={handleAddBook}
                    onRemoveBook={handleDeleteBook}
                    onClose={handleCloseModelBooksDrawer}
                />
            )}
            {isMediaDrawer && (
                <AddMediaDrawer
                    onClose={handleCloseAddMediaDrawer}
                    modelId={modelId}
                    packageId={currentPackage.ID}
                    packageGroup={packageGroup}
                />
            )}
        </>
    );
};

export default ModelsTableColumnPackage;
